export type PreferenceNamespace =
    | "grid.views"
    | "grid.views.order"
    | "grid.views.selected"
    | "grid.columns.visibility"
    | "grid.columns.order"
    | "grid.columns.width"
    | "ui"
    | "ui.tabs";

type PreferenceEntry = {
    namespace: PreferenceNamespace;
    preferenceKey: string;
    value: unknown;
};

async function getApiClient() {
    const { loggedInApiCall } = await import("src/api/axiosClient/userApi");
    return loggedInApiCall;
}

const SYNC_MARKER_KEY = "fp_preferences_synced";
const LOCAL_CACHE_PREFIX = "fp_pref_";
const PREFERENCE_PUT_DEBOUNCE_MS = 400;

const pendingPutWrites = new Map<string, ReturnType<typeof setTimeout>>();

function buildLocalCacheKey(namespace: string, key: string): string {
    return `${LOCAL_CACHE_PREFIX}${namespace}::${key}`;
}

function buildPendingWriteKey(namespace: PreferenceNamespace, key: string): string {
    return `${namespace}::${key}`;
}

function serializePreferenceValue(value: unknown): string {
    return JSON.stringify(value);
}

function schedulePreferencePut(namespace: PreferenceNamespace, key: string, cacheKey: string): void {
    const pendingKey = buildPendingWriteKey(namespace, key);
    const existingTimeout = pendingPutWrites.get(pendingKey);
    if (existingTimeout !== undefined) {
        clearTimeout(existingTimeout);
    }

    pendingPutWrites.set(
        pendingKey,
        setTimeout(() => {
            pendingPutWrites.delete(pendingKey);

            const latestSerialized = localStorage.getItem(cacheKey);
            if (latestSerialized === null) {
                return;
            }

            let latestValue: unknown;
            try {
                latestValue = JSON.parse(latestSerialized) as unknown;
            } catch {
                return;
            }

            const url = `/api/users/me/preferences/${encodeURIComponent(namespace)}/${encodeURIComponent(key)}`;
            getApiClient()
                .then((client) =>
                    client.put(url, { value: latestValue }, { headers: { "Content-Type": "application/ld+json" } }),
                )
                .catch((error) => {
                    console.warn(`[PreferenceStore] Failed to persist preference ${namespace}/${key}:`, error);
                });
        }, PREFERENCE_PUT_DEBOUNCE_MS),
    );
}

/**
 * Read a preference value. Uses localStorage cache first for synchronous access,
 * then returns server value when available.
 */
export function getPreferenceFromCache<T = unknown>(namespace: PreferenceNamespace, key: string): T | null {
    const cacheKey = buildLocalCacheKey(namespace, key);
    const cached = localStorage.getItem(cacheKey);
    if (cached === null) return null;

    try {
        return JSON.parse(cached) as T;
    } catch {
        return null;
    }
}

/**
 * Write a preference to both localStorage cache and server.
 * localStorage is updated synchronously for immediate UI feedback.
 * Server write is async and fire-and-forget (errors are logged but don't block UI).
 */
export function setPreference(namespace: PreferenceNamespace, key: string, value: unknown): void {
    const cacheKey = buildLocalCacheKey(namespace, key);
    const serialized = serializePreferenceValue(value);
    const cached = localStorage.getItem(cacheKey);

    if (cached === serialized) {
        return;
    }

    localStorage.setItem(cacheKey, serialized);
    schedulePreferencePut(namespace, key, cacheKey);
}

/**
 * Delete a preference from both localStorage cache and server.
 */
export function deletePreference(namespace: PreferenceNamespace, key: string): void {
    const cacheKey = buildLocalCacheKey(namespace, key);
    localStorage.removeItem(cacheKey);

    const url = `/api/users/me/preferences/${encodeURIComponent(namespace)}/${encodeURIComponent(key)}`;
    getApiClient()
        .then((client) => client.delete(url))
        .catch((error) => {
            console.warn(`[PreferenceStore] Failed to delete preference ${namespace}/${key}:`, error);
        });
}

/**
 * Load all preferences from server and populate the localStorage cache.
 * Called once at app init after authentication.
 */
export async function loadPreferencesFromServer(): Promise<void> {
    try {
        const client = await getApiClient();
        const response = await client.get("/api/users/me/preferences");
        const data = response.data;

        const members: PreferenceEntry[] = Array.isArray(data) ? data : (data?.["hydra:member"] ?? []);

        for (const pref of members) {
            const cacheKey = buildLocalCacheKey(pref.namespace, pref.preferenceKey);
            localStorage.setItem(cacheKey, JSON.stringify(pref.value));
        }
    } catch (error) {
        console.warn("[PreferenceStore] Failed to load preferences from server:", error);
    }
}

/**
 * Build the legacy localStorage key -> (namespace, preferenceKey) mapping.
 * Used during the one-time migration from localStorage to server.
 */
type LegacyKeyMapping = {
    legacyKey: string;
    namespace: PreferenceNamespace;
    preferenceKey: string;
    transform?: (value: string) => unknown;
};

const SIMPLE_UI_KEYS: Array<{ legacyKey: string; preferenceKey: string }> = [
    { legacyKey: "sidebarStatus", preferenceKey: "sidebarStatus" },
    { legacyKey: "catalogView", preferenceKey: "catalogView" },
    { legacyKey: "displayRegulatoryInfoBox", preferenceKey: "displayRegulatoryInfoBox" },
    { legacyKey: "lastSelectedCompanyScore", preferenceKey: "lastSelectedCompanyScore" },
    { legacyKey: "currentCampaignId", preferenceKey: "currentCampaignId" },
    { legacyKey: "hideEngagedActions", preferenceKey: "hideEngagedActions" },
];

const TAB_KEYS: Array<{ legacyKey: string; preferenceKey: string }> = [
    { legacyKey: "actionPlan-main-tabs", preferenceKey: "actionPlan-main" },
    { legacyKey: "actionPlan-trajectory-tabs", preferenceKey: "actionPlan-trajectory" },
    { legacyKey: "actionPlan-planning-tabs", preferenceKey: "actionPlan-planning" },
    { legacyKey: "actionPlan-catalog-tabs", preferenceKey: "actionPlan-catalog" },
];

function buildLegacyMappings(): LegacyKeyMapping[] {
    const mappings: LegacyKeyMapping[] = [];

    for (const { legacyKey, preferenceKey } of SIMPLE_UI_KEYS) {
        mappings.push({
            legacyKey,
            namespace: "ui",
            preferenceKey,
            transform: (v) => v,
        });
    }

    for (const { legacyKey, preferenceKey } of TAB_KEYS) {
        mappings.push({
            legacyKey,
            namespace: "ui.tabs",
            preferenceKey,
            transform: (v) => v,
        });
    }

    return mappings;
}

function discoverGridKeys(): string[] {
    const registeredRaw = localStorage.getItem("registered-grid");
    if (registeredRaw === null) return [];

    try {
        const parsed = JSON.parse(registeredRaw);
        if (!Array.isArray(parsed)) return [];
        return parsed as string[];
    } catch {
        return [];
    }
}

/**
 * One-time migration: reads all relevant localStorage keys, uploads them
 * to the server, then marks migration as complete.
 */
export async function migrateLocalStorageToServer(): Promise<void> {
    if (localStorage.getItem(SYNC_MARKER_KEY) !== null) {
        return;
    }

    const preferences: PreferenceEntry[] = [];

    const staticMappings = buildLegacyMappings();
    for (const mapping of staticMappings) {
        const raw = localStorage.getItem(mapping.legacyKey);
        if (raw === null) continue;

        const value = mapping.transform ? mapping.transform(raw) : raw;
        preferences.push({
            namespace: mapping.namespace,
            preferenceKey: mapping.preferenceKey,
            value,
        });
    }

    const gridKeys = discoverGridKeys();
    for (const visibilityKey of gridKeys) {
        const visibilityRaw = localStorage.getItem(visibilityKey);
        if (visibilityRaw !== null) {
            try {
                preferences.push({
                    namespace: "grid.columns.visibility",
                    preferenceKey: visibilityKey,
                    value: JSON.parse(visibilityRaw),
                });
            } catch {
                /* skip malformed */
            }
        }

        const orderRaw = localStorage.getItem(`${visibilityKey}-order`);
        if (orderRaw !== null) {
            try {
                preferences.push({
                    namespace: "grid.columns.order",
                    preferenceKey: visibilityKey,
                    value: JSON.parse(orderRaw),
                });
            } catch {
                /* skip malformed */
            }
        }

        const widthRaw = localStorage.getItem(`witdth-${visibilityKey}`);
        if (widthRaw !== null) {
            try {
                preferences.push({
                    namespace: "grid.columns.width",
                    preferenceKey: visibilityKey,
                    value: JSON.parse(widthRaw),
                });
            } catch {
                /* skip malformed */
            }
        }
    }

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key === null || !key.startsWith("viewFilters-")) continue;

        const raw = localStorage.getItem(key);
        if (raw === null) continue;

        const gridKey = key.replace("viewFilters-", "");
        try {
            const parsed = JSON.parse(raw);
            preferences.push({
                namespace: "grid.views",
                preferenceKey: gridKey,
                value: parsed,
            });

            const viewNames = Object.keys(parsed).sort();
            if (viewNames.length > 0) {
                preferences.push({
                    namespace: "grid.views.order",
                    preferenceKey: gridKey,
                    value: viewNames,
                });
            }
        } catch {
            /* skip malformed */
        }
    }

    if (preferences.length === 0) {
        localStorage.setItem(SYNC_MARKER_KEY, new Date().toISOString());
        return;
    }

    try {
        const client = await getApiClient();
        const batchSize = 100;
        for (let i = 0; i < preferences.length; i += batchSize) {
            const batch = preferences.slice(i, i + batchSize);
            await client.post(
                "/api/users/me/preferences/bulk",
                { preferences: batch },
                { headers: { "Content-Type": "application/ld+json" } },
            );
        }

        for (const pref of preferences) {
            const cacheKey = buildLocalCacheKey(pref.namespace, pref.preferenceKey);
            localStorage.setItem(cacheKey, JSON.stringify(pref.value));
        }

        localStorage.setItem(SYNC_MARKER_KEY, new Date().toISOString());
    } catch (error) {
        console.warn("[PreferenceStore] Migration failed, will retry on next app load:", error);
    }
}
